import React, { useState, useEffect } from 'react';
import { FileText, Mic, CheckCircle, Edit } from 'lucide-react';
import Swal from 'sweetalert2';

const roboman_api = process.env.REACT_APP_ROBOMAN_API;

const Loader = ({ className }) => (
  <div className={`w-5 h-5 border-2 border-slate-500 border-t-transparent rounded-full animate-spin ${className}`}></div>
);

const FormInput = ({ id, label, value, onChange, placeholder, type = "text", required = false }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      id={id}
      name={id}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="block w-full px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm placeholder-slate-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
    />
  </div>
);

const UpdateMetadataStep = ({ onMetadataSubmit, disabled, selectedTemplate, uploadedFileId }) => {
  const [formData, setFormData] = useState({
    client_name: '',
    client_designation: '',
    client_number: '',
    client_address: '',
    client_company: '',
    calendly_link: '',
  });
  const [preselected, setPreselected] = useState({ voiceId: 'Loading...', templateId: 'Loading...' });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const safeJSONParse = (jsonString) => {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.error("Failed to parse JSON string:", e);
      return null;
    }
  };

  // Initialize form data once on mount
  useEffect(() => {
    const savedFormData = safeJSONParse(localStorage.getItem('clientMetadata'));

    if (savedFormData) {
      setFormData(savedFormData);
      setIsSaved(true);
    } else {
      const userData = safeJSONParse(localStorage.getItem("user_data"));
      const initialData = {
        client_name: userData?.user_info?.client_name || '',
        client_designation: userData?.user_info?.client_designation || '',
        client_number: userData?.user_info?.client_number || '',
        client_address: userData?.user_info?.client_address || '',
        client_company: userData?.user_info?.client_company || '',
        calendly_link: userData?.user_info?.calendly_link || '',
      };
      setFormData(initialData);
    }
  }, []);

  // Update preselected voice/template display when selections change
  useEffect(() => {
    const savedVoiceData = safeJSONParse(localStorage.getItem('voiceUploadResponse'));
    const savedTemplate = localStorage.getItem('selectedTemplateId');
    const userData = safeJSONParse(localStorage.getItem("user_data"));

    setPreselected({
      voiceId: savedVoiceData?.voice_id || userData?.user_info?.voice_note_id || 'Not Found',
      templateId: savedTemplate || userData?.user_info?.proposal_template || 'Not Found',
    });
  }, [selectedTemplate, uploadedFileId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setIsSaved(false); // Any change marks the data as unsaved
  };

  const handleSaveClick = async () => {
    setIsLoading(true);

    const userData = safeJSONParse(localStorage.getItem("user_data"));
    const voiceData = safeJSONParse(localStorage.getItem('voiceUploadResponse'));
    const selectedTemplateId = localStorage.getItem('selectedTemplateId');
    const campaignIdFromStorage = localStorage.getItem('campaign_id');

    const voiceNoteId =
      voiceData?.voice_id ?? userData?.user_info?.voice_note_id ?? '';
    const proposalTemplate =
      selectedTemplateId ?? userData?.user_info?.proposal_template ?? '';
    const campaignId =
      campaignIdFromStorage || userData?.user_info?.campaign_id || '';

    // Existing payload for /users/me/update-user-metadata
    const userMetadataPayload = {
      ...formData,
      user_id: userData?.user_id,
      voice_note_id: voiceNoteId,
      proposal_template: proposalTemplate,
    };

    // NEW: payload for /campaign/update_metadata (CampaignMetadataUpdate)
    const campaignMetadataPayload = {
      client_name: formData.client_name,
      client_designation: formData.client_designation,
      client_number: formData.client_number,
      client_address: formData.client_address,
      client_company: formData.client_company,
      campaign_id: campaignId,
      voice_note_id: voiceNoteId,
      calendly_link: formData.calendly_link,
      proposal_template: proposalTemplate,
    };

    try {
      const token = localStorage.getItem('access_token');

      // 1) Call existing user metadata API
      const userRes = await fetch(`${roboman_api}/users/me/update-user-metadata`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(userMetadataPayload),
      });

      if (!userRes.ok) {
        const errorBody = await userRes.text();
        throw new Error(`User metadata API Error: ${userRes.status} - ${errorBody}`);
      }

      const userResponseData = await userRes.json();
      if (!userResponseData.success) {
        throw new Error("User metadata API returned an unsuccessful status.");
      }

      // 2) Call extra /campaign/update_metadata API
      const campaignRes = await fetch(`${roboman_api}/campaign/update_metadata`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          // backend expects request_user_id header
          'request_user_id': userData?.user_id || '',
        },
        body: JSON.stringify(campaignMetadataPayload),
      });

      if (!campaignRes.ok) {
        const errorBody = await campaignRes.text();
        throw new Error(`Campaign metadata API Error: ${campaignRes.status} - ${errorBody}`);
      }

      const campaignResponseData = await campaignRes.json();
      if (campaignResponseData.status !== 'success') {
        throw new Error(campaignResponseData?.Error || 'Campaign metadata update failed');
      }

      // Cache form data locally for next visit
      localStorage.setItem('clientMetadata', JSON.stringify(formData));

      Swal.fire({
        icon: 'success',
        title: 'Details Saved!',
        showConfirmButton: false,
        timer: 1500,
      });

      setIsSaved(true);
      onMetadataSubmit(formData);

    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Save Failed', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const isButtonDisabled =
    disabled || isLoading || isSaved || !formData.client_name.trim() || !formData.client_company.trim();

  return (
    <div className="space-y-6">
      {isSaved && (
        <div className="p-4 bg-green-100 border-l-4 border-green-500 text-green-700 flex justify-between items-center">
          <div className="flex items-center">
            <CheckCircle className="mr-3" />
            <p className="font-bold">Client details are saved and ready for the next step.</p>
          </div>
          <button
            onClick={() => setIsSaved(false)}
            className="flex items-center text-sm font-semibold hover:underline"
          >
            <Edit size={14} className="mr-1" /> Edit
          </button>
        </div>
      )}

      <div className="p-4 bg-slate-50 border rounded-lg space-y-3">
        <div className="flex items-center text-sm text-slate-600">
          <Mic size={16} className="mr-3 text-violet-500" />
          <strong>Selected Voice :</strong>
          <span className="ml-2 font-mono text-xs bg-slate-200 py-1 px-2 rounded">
            {preselected.voiceId&&"Voice successfully uploaded"}
          </span>
        </div>
        <div className="flex items-center text-sm text-slate-600">
          <FileText size={16} className="mr-3 text-violet-500" />
          <strong>Selected Template :</strong>
          <span className="ml-2 font-mono text-xs bg-slate-200 py-1 px-2 rounded">
            {preselected.templateId}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormInput id="client_name" label="Name" value={formData.client_name} onChange={handleChange} required />
        <FormInput id="client_designation" label="Designation" value={formData.client_designation} onChange={handleChange} />
        <FormInput id="client_company" label="Company" value={formData.client_company} onChange={handleChange} required />
        <FormInput id="client_number" label="Number" value={formData.client_number} onChange={handleChange} type="tel" />
        <FormInput id="client_address" label="Address" value={formData.client_address} onChange={handleChange} />
        <FormInput id="calendly_link" label="Calendly Link" value={formData.calendly_link} onChange={handleChange} type="url" />
      </div>

      <div className="pt-4 border-t">
        <button
          onClick={handleSaveClick}
          className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-semibold py-3 px-8 rounded-lg shadow-md hover:bg-fuchsia-700 transition-all disabled:bg-violet-500 disabled:opacity-80 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          disabled={isButtonDisabled}
        >
          {isLoading ? (
            <Loader className="!w-5 !h-5 !border-white !border-t-transparent" />
          ) : (
            isSaved ? <CheckCircle size={20} /> : null
          )}
          <span>{isLoading ? 'Saving...' : isSaved ? 'Details Saved' : 'Save Details'}</span>
        </button>
      </div>
    </div>
  );
};

export default UpdateMetadataStep;
